import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/axios";
import { FaPills, FaCheck, FaTimes, FaRedo, FaSpinner } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./ModifierProduitPharmaceutique.css";

export default function ModifierProduitPharmaceutique() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const TYPE_PRODUIT_OPTIONS = useMemo(() => [
    { value: 'vaccin', label: t.vaccine },
    { value: 'vermifuge', label: t.deworming },
    { value: 'antibiotique', label: t.antibiotic },
    { value: 'vitamine', label: t.vitamin },
    { value: 'desinfectant', label: t.disinfectant },
    { value: 'autre', label: t.other },
  ], [t]);

  const UNITE_OPTIONS = useMemo(() => [
    { value: 'ml', label: 'ml' },
    { value: 'comprime', label: t.tablet },
    { value: 'flacon', label: t.bottle },
    { value: 'dose', label: t.dose },
    { value: 'kg', label: 'kg' },
    { value: 'g', label: 'g' },
    { value: 'unite', label: t.unitLabel },
  ], [t]);
  const [formData, setFormData] = useState({
    nom: "",
    type_produit: "",
    unite: "",
    description: "",
    actif: true,
  });
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    nom: "",
    type_produit: "",
    unite: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    nom: false,
    type_produit: false,
    unite: false,
  });

  const fetchProduit = async () => {
    try {
      const response = await api.get(`/api/produits-pharmaceutiques/${id}/`);
      const clonedData = typeof structuredClone !== 'undefined'
        ? structuredClone(response.data)
        : JSON.parse(JSON.stringify(response.data));

      setFormData(response.data);
      setInitialData(clonedData);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement du produit:", error);
      let errorMessage = "Impossible de charger le produit.";
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Le produit demandé n'existe pas.";
        } else if (error.response.status === 403) {
          errorMessage = "Vous n'avez pas la permission d'accéder à ce produit.";
        } else if (error.response.status >= 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
        } else {
          errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduit();
  }, [id]);

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "nom" || name === "type_produit" || name === "unite") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData({
      ...formData,
      [name]: fieldValue,
    });

    if ((name === "nom" || name === "type_produit" || name === "unite") && (touchedFields[name] || fieldErrors[name])) {
      const error = validateField(name, fieldValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }

    if (name === "nom" || name === "type_produit" || name === "unite") {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newTouchedFields = {
      nom: true,
      type_produit: true,
      unite: true,
    };
    setTouchedFields(newTouchedFields);

    const newFieldErrors = {
      nom: validateField("nom", formData.nom),
      type_produit: validateField("type_produit", formData.type_produit),
      unite: validateField("unite", formData.unite),
    };
    setFieldErrors(newFieldErrors);

    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");

    if (hasErrors) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.patch(`/api/produits-pharmaceutiques/${id}/`, formData);
      setShowConfirmModal(false);
      toast.success("Produit pharmaceutique modifié avec succès !");
      setTimeout(() => {
        navigate("/produits-pharmaceutiques");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(", ") ||
        "Erreur lors de la modification du produit !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleReset = () => {
    if (initialData) {
      setFormData(initialData);
      setFieldErrors({
        nom: "",
        type_produit: "",
        unite: "",
      });
      setTouchedFields({
        nom: false,
        type_produit: false,
        unite: false,
      });
      toast.info("Formulaire réinitialisé aux valeurs d'origine.");
    }
  };

  const isFormValid = () => {
    if (!formData.nom || !formData.nom.trim()) return false;
    if (!formData.type_produit || !formData.type_produit.trim()) return false;
    if (!formData.unite || !formData.unite.trim()) return false;
    return true;
  };

  const hasChanges = () => {
    if (!initialData) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  };

  if (loading) {
    return (
      <div className="modifier-produit-pharmaceutique-page">
        <div className="loading-container">
          <FaSpinner className="spinning" />
          <p>Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modifier-produit-pharmaceutique-page">
        <div className="title-badge">
          <FaPills className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button
            onClick={() => navigate("/produits-pharmaceutiques")}
            className="back-button"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-produit-pharmaceutique-page">
      <div className="title-badge">
        <FaPills className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER UN PRODUIT PHARMACEUTIQUE</span>
      </div>

      <form onSubmit={handleSubmit} className="modifier-produit-form">
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="nom">
              Nom du produit <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              placeholder={t.productNameLabel}
              value={formData.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.nom && touchedFields.nom ? "input-error" : ""}
              maxLength={191}
            />
            {fieldErrors.nom && touchedFields.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="type_produit">
              {t.productTypeLabel} <span className="required-indicator">*</span>
            </label>
            <select
              id="type_produit"
              name="type_produit"
              value={formData.type_produit}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_produit && touchedFields.type_produit ? "input-error" : ""}
            >
              <option value="">{t.selectType}</option>
              {TYPE_PRODUIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldErrors.type_produit && touchedFields.type_produit && (
              <span className="field-error-message">{fieldErrors.type_produit}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="unite">
              Unité <span className="required-indicator">*</span>
            </label>
            <select
              id="unite"
              name="unite"
              value={formData.unite}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.unite && touchedFields.unite ? "input-error" : ""}
            >
              <option value="">{t.selectUnit}</option>
              {UNITE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldErrors.unite && touchedFields.unite && (
              <span className="field-error-message">{fieldErrors.unite}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="description">{t.description}</label>
            <textarea
              id="description"
              name="description"
              placeholder={t.productDescriptionOptional}
              value={formData.description || ""}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form">
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="actif"
                name="actif"
                checked={formData.actif}
                onChange={handleChange}
              />
              <span>Produit actif</span>
            </label>
          </div>
        </div>

        <div className="actions">
          <button
            type="button"
            className="reset-but"
            onClick={handleReset}
            disabled={!hasChanges() || isSubmitting}
            title="Réinitialiser le formulaire"
          >
            <FaRedo />
            <span>Réinitialiser</span>
          </button>
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || isSubmitting || !hasChanges()}
          >
            <FaCheck />
            <span>{isSubmitting ? "Enregistrement..." : "Enregistrer"}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/produits-pharmaceutiques")}
            disabled={isSubmitting}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer la modification</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer la modification :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Nom :</span>
                  <span className="summary-value">{formData.nom}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Type :</span>
                  <span className="summary-value">{TYPE_PRODUIT_OPTIONS.find(opt => opt.value === formData.type_produit)?.label || formData.type_produit}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Unité :</span>
                  <span className="summary-value">{UNITE_OPTIONS.find(opt => opt.value === formData.unite)?.label || formData.unite}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={isSubmitting}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={isSubmitting}
              >
                <FaCheck />
                <span>{isSubmitting ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}